/**
 * stores/auth
 * **/

import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { authFetch } from '@/composables/utils'

export interface User {
  username: string
  email: string
  permissions: string[]
}

export interface Tokens {
  access: string
  refresh: string
}

interface AuthState {
  user: User | null
  tokens: Tokens | null
  isAuthenticated: boolean
}

const performRequest = async (url: string, body: object) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // 'X-API-KEY': import.meta.env.VITE_API_KEY,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(errorData.detail || 'La richiesta è fallita')
  }
  return res.json() as Promise<Tokens>
}

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const baseUrl = import.meta.env.VITE_API_URL

  const state: AuthState = reactive({
    user: null,
    tokens: null,
    isAuthenticated: false,
  })

  // Timer reference for silent refresh
  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  const permissions = computed(() => state.user?.permissions || [])

  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  /**
   * Start a timer to refresh the token automatically before it expires.
   * Access token lifetime is 15 minutes, so we refresh at 14 minutes.
   */
  function startRefreshTimer() {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(
      async () => {
        try {
          await refreshToken()
        } catch (e) {
          console.warn('Silent refresh failed, will retry on next request via interceptor.', e)
        }
      },
      14 * 60 * 1000,
    ) // 14 minutes
  }

  function stopRefreshTimer() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  async function _updateStateWithTokens(tokens: Tokens) {
    state.tokens = tokens
    localStorage.setItem('tokens', JSON.stringify(tokens))
    startRefreshTimer() // Restart the timer
    await fetchSession()
  }

  async function fetchSession() {
    try {
      const session = await authFetch('/api/auth/session/')
      if (session && (session as { isAuthenticated: boolean }).isAuthenticated) {
        state.user = {
          username: (session as { username: string }).username,
          email: (session as { email: string }).email,
          permissions: (session as { permissions: string[] }).permissions,
        }
        state.isAuthenticated = true
      } else {
        logout()
      }
    } catch (error) {
      console.error('Session fetch failed:', error)
      logout()
    }
  }

  async function restoreSession(): Promise<void> {
    const tokensJson = localStorage.getItem('tokens')
    if (tokensJson) {
      try {
        const tokens: Tokens = JSON.parse(tokensJson)
        state.tokens = tokens
        startRefreshTimer() // Start timer on restore
        await fetchSession()
      } catch {
        logout()
      }
    }
  }

  async function login(username: string, password: string): Promise<void> {
    const tokens = await performRequest(`${baseUrl}/auth/login/`, { username, password })
    await _updateStateWithTokens(tokens)
    router.push('/')
  }

  async function refreshToken(): Promise<void> {
    if (!state.tokens?.refresh) {
      logout()
      throw new Error('Nessun refresh token disponibile.')
    }
    try {
      // Direct fetch to avoid circular dependency
      const tokens = await performRequest(`${baseUrl}/auth/refresh/`, {
        refresh: state.tokens.refresh,
      })
      await _updateStateWithTokens(tokens)
    } catch (error) {
      console.error('Refresh fallito:', error)
      logout()
      throw error
    }
  }

  function logout(): void {
    stopRefreshTimer()
    state.user = null
    state.tokens = null
    state.isAuthenticated = false
    localStorage.removeItem('tokens')
    router.push('/login')
  }

  function getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // 'X-API-KEY': import.meta.env.VITE_API_KEY,
    }
    if (state.tokens?.access) {
      headers['Authorization'] = `Bearer ${state.tokens.access}`
    }
    return headers
  }

  return {
    user: computed(() => state.user),
    isAuthenticated: computed(() => state.isAuthenticated),
    restoreSession,
    login,
    refreshToken,
    logout,
    getAuthHeaders,
    hasPermission,
    fetchSession,
  }
})
